import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import {
    editRightsholder,
    getAllPayees,
    getClaimedShares,
    getRightsholderSummary,
} from '@App/api/routes/rightsholder/rightsholder'
import { RightsholderClaimedShareList } from '@Entities/rightsholder'
import RootStore from '@App/stores/root-store'
import { Payee, Payees } from '@Entities/rightsholder/payee'
import { AxiosResponse } from 'axios'
import { SelectOption } from '@Components/Selectors/Select'
import { MemberSummary } from '@Entities/member/member-summary'
import { RightsholderRestrictionStatus } from '@App/enum/rightsholder-restriction-status'

class CurrentRightsholderStore {
    private rootStore: RootStore

    private _currentRightsholderId: number | undefined

    private _rightsholderClaimedShares: RightsholderClaimedShareList

    private _memberSummary: MemberSummary | undefined

    private _payees: Payee[]

    constructor(rootStore: RootStore) {
        makeObservable<
            CurrentRightsholderStore,
            '_currentRightsholderId' | '_rightsholderClaimedShares' | '_payees' | '_memberSummary' | 'isRestricted'
        >(this, {
            _currentRightsholderId: observable,
            _rightsholderClaimedShares: observable,
            _payees: observable,
            _memberSummary: observable,
            currentRightsholder: computed,
            isRestricted: computed,
            payeeOptions: computed,
            setCurrentRightsholderId: action,
            destroyRightsholder: action,
            resetClaimedShares: action,
        })

        this.rootStore = rootStore
        this._payees = []
    }

    get currentRightsholderId(): number {
        if (this._currentRightsholderId) {
            return this._currentRightsholderId
        } else {
            throw new Error('Rightsholder ID Is Missing')
        }
    }

    get optionalCurrentRightsholderId(): number | undefined {
        return this._currentRightsholderId
    }

    get rightsholderClaimedShares() {
        return this._rightsholderClaimedShares
    }

    findSpecificWorkClaimedShares = (workId: number | null | undefined) =>
        this._rightsholderClaimedShares?.workClaimedShares?.find((share) => share.id === workId)
            ?.rightsholderClaimedShare

    get memberSummary(): MemberSummary {
        if (this._memberSummary) {
            return this._memberSummary
        } else {
            throw new Error('Member Summary Is Missing')
        }
    }

    get isRestricted() {
        return (
            this._memberSummary?.restrictionStatus &&
            this._memberSummary?.restrictionStatus !== RightsholderRestrictionStatus.NONE
        )
    }

    get optionalMemberSummary() {
        return this._memberSummary
    }

    get currentRightsholder() {
        const allDoneRightsHolders = this.rootStore.rightsholderSearchStore.rightsholders
        const currentRightsHolderId = this.optionalCurrentRightsholderId

        if (allDoneRightsHolders && currentRightsHolderId) {
            return allDoneRightsHolders.content.find((item: MemberSummary) => item.ipId === currentRightsHolderId)
        }

        return null
    }

    get payeeOptions(): SelectOption[] {
        return this._payees.map((payee) => ({
            id: payee.payeeIpId.toString(),
            value: payee.payeeIpId.toString(),
            label: payee.payeeName,
        }))
    }

    public setCurrentRightsholderId = async (rightsholderId: number) => {
        if (this._currentRightsholderId !== rightsholderId) {
            this.rootStore.preloaderStore.openTransparentPreloader(true)
            this._currentRightsholderId = rightsholderId
            await this.loadRightsholderSummary()
            this.rootStore.preloaderStore.openTransparentPreloader(false)
        }
    }

    public loadClaimedShares = async (searchWorkIds: number[]): Promise<void> => {
        const response = await getClaimedShares(this.currentRightsholderId, searchWorkIds)
        runInAction(() => (this._rightsholderClaimedShares = response.data))
    }

    public loadRightsholderSummary = async (): Promise<void> => {
        const response = await getRightsholderSummary(this.currentRightsholderId)
        runInAction(() => (this._memberSummary = response.data))
    }

    public editRightsholder = async (rightsholderId: number, data: Partial<MemberSummary>): Promise<void> => {
        await editRightsholder(rightsholderId, data)
        this.memberSummary.pending = true
        const userInfo = this.rootStore.userStore.userInfo
        const isInternal = userInfo && userInfo.isInternal
        if (!isInternal) {
            await this.rootStore.rightsholderSearchStore.loadRightsholders({})
        }
    }

    public destroyRightsholder(): void {
        this._currentRightsholderId = undefined
        this._memberSummary = undefined
    }

    public resetClaimedShares(): void {
        this._rightsholderClaimedShares = { workClaimedShares: [] }
    }

    public loadPayees = async (): Promise<void> => {
        const payees: AxiosResponse<Payees> = await getAllPayees(this.currentRightsholderId)
        runInAction(() => (this._payees = payees.data.data))
    }
}

export default CurrentRightsholderStore
